import React from "react";

import { LogoutOutlined } from "@ant-design/icons";
import { Button } from "antd";
import {
  ArrowRight,
  Sparkles,
  FlaskConical,
  TrendingUp,
  Users,
} from "lucide-react";

import { Logo } from "@falcon-terminal/shared-ui/components/common";

import type { FalconTerminalProps } from "../types/terminal.types";

type InputStudio = {
  id: "strategy" | "innovation";
  name: string;
  description: string;
  audience: string[];
  icon: React.ComponentType<{ className?: string }>;
  theme: "blue" | "gold";
  gradient: string;
  iconGlow: string;
  borderGlow: string;
};

type OutputStudio = {
  id: "insights";
  name: string;
  description: string;
  audience: string[];
  icon: React.ComponentType<{ className?: string }>;
  theme: "mixed";
  gradient: string;
  iconGlow: string;
  borderGlow: string;
};

const INPUT_STUDIOS: InputStudio[] = [
  {
    id: "strategy",
    name: "Strategy Studio",
    description:
      "Strategic planning and decision-making tools for executive leadership",
    audience: ["C-suite", "CPO", "Executives"],
    icon: Users,
    theme: "blue",
    gradient: "from-[--color-blue]/25 via-[--color-blue-alt]/15 to-transparent",
    iconGlow: "shadow-[0_0_30px_rgba(28,30,123,0.4)]",
    borderGlow: "hover:shadow-[0_0_40px_rgba(28,30,123,0.3)]",
  },
  {
    id: "innovation",
    name: "Innovation Studio",
    description:
      "User research and experiment validation platform for product teams",
    audience: ["Product Managers", "UX Researchers"],
    icon: FlaskConical,
    theme: "gold",
    gradient:
      "from-[--color-gold]/25 via-[--color-gold-soft]/15 to-transparent",
    iconGlow: "shadow-[0_0_30px_rgba(212,175,55,0.4)]",
    borderGlow: "hover:shadow-[0_0_40px_rgba(212,175,55,0.3)]",
  },
];

const OUTPUT_STUDIO: OutputStudio = {
  id: "insights",
  name: "Insights Command Center",
  description:
    "Manage your experiment portfolio, view results, and access detailed insights from validation efforts",
  audience: ["Data Analysts", "Product Managers", "Executives"],
  icon: TrendingUp,
  theme: "mixed",
  gradient: "from-[--color-blue]/15 via-[--color-gold]/10 to-[--color-blue]/5",
  iconGlow: "shadow-[0_0_25px_rgba(212,175,55,0.25)]",
  borderGlow: "hover:shadow-[0_0_50px_rgba(28,30,123,0.2)]",
};

export const FalconTerminal: React.FC<FalconTerminalProps> = ({
  onStudioSelect,
  onLogout,
}) => {
  return (
    <div className="min-h-screen bg-[--color-bg-black] text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-cinematic opacity-60" />
      <div className="absolute inset-0 bg-noise-subtle opacity-30" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[800px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(28,30,123,0.15)_0%,transparent_70%)] pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 md:px-16 lg:px-28 py-8 md:py-12">
        <div className="flex justify-end mb-4">
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={onLogout}
            className="!text-white/30 hover:!text-white/60 !text-[12px] font-medium uppercase tracking-wider"
          >
            Logout
          </Button>
        </div>

        <div
          className="text-center mb-8 animate-fade-in"
          style={{ animationDelay: "0ms", animationFillMode: "both" }}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="relative">
              <div className="absolute inset-0 blur-xl bg-[--color-gold]/30 rounded-full scale-150 animate-pulse" />
              <div className="relative z-10 drop-shadow-[0_0_20px_rgba(212,175,55,0.5)]">
                <Logo variant="logomark" height={56} />
              </div>
            </div>
          </div>

          <h1 className="heading-artistic text-4xl md:text-5xl font-poppins font-bold mb-4">
            <span className="text-gradient-falcon-gold">Falcon Terminal</span>
          </h1>

          <p className="text-base md:text-lg text-[--text-muted] max-w-2xl mx-auto leading-relaxed">
            Select your studio to begin. Each environment is tailored for
            specific roles and workflows.
          </p>
        </div>

        <div className="mb-8">
          <div
            className="flex items-center gap-3 mb-4 animate-fade-in"
            style={{ animationDelay: "100ms", animationFillMode: "both" }}
          >
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <span className="text-xs uppercase tracking-[0.2em] text-[--text-muted] font-medium">
              Build & Validate
            </span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {INPUT_STUDIOS.map((studio, index) => {
              const IconComponent = studio.icon;
              const animationDelay = `${150 + index * 100}ms`;

              return (
                <div
                  key={studio.id}
                  className={`
                    group relative rounded-lg border border-white/10 backdrop-blur-xl
                    bg-gradient-to-br ${studio.gradient} bg-white/[0.02]
                    transition-all duration-500 ease-out cursor-pointer
                    hover:border-white/20 hover:scale-[1.02] hover:-translate-y-1
                    ${studio.borderGlow}
                    animate-fade-in
                  `}
                  style={{
                    animationDelay,
                    animationFillMode: "both",
                    animationDuration: "600ms",
                  }}
                  onClick={() => onStudioSelect?.(studio.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onStudioSelect?.(studio.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />

                  <div className="relative p-6">
                    <div
                      className={`
                        w-14 h-14 rounded-lg flex items-center justify-center mb-4
                        bg-white/5 border border-white/10
                        transition-all duration-500
                        group-hover:bg-white/10 group-hover:border-white/20
                        ${studio.iconGlow}
                      `}
                    >
                      <IconComponent
                        className={`w-7 h-7 ${
                          studio.theme === "gold"
                            ? "text-[--color-gold]"
                            : "text-[--color-blue]"
                        } transition-transform duration-300 group-hover:scale-110`}
                      />
                    </div>

                    <h3 className="font-poppins font-bold text-xl text-white mb-2 tracking-tight">
                      {studio.name}
                    </h3>

                    <p className="text-[--text-muted] mb-4 leading-relaxed text-sm">
                      {studio.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-5">
                      {studio.audience.map((role) => (
                        <span
                          key={role}
                          className={`
                            text-xs px-2.5 py-1 rounded-full
                            ${
                              studio.theme === "gold"
                                ? "bg-[--color-gold]/10 text-[--color-gold]/80 border border-[--color-gold]/20"
                                : "bg-[--color-blue]/10 text-white/70 border border-[--color-blue]/20"
                            }
                          `}
                        >
                          {role}
                        </span>
                      ))}
                    </div>

                    <button
                      type="button"
                      className={`
                        w-full py-2 rounded-lg font-medium text-sm text-white
                        bg-white/5 border border-white/10
                        transition-all duration-300 group/btn
                        hover:bg-white/15 hover:border-white/25
                        ${
                          studio.theme === "gold"
                            ? "hover:border-[--color-gold]/40 hover:text-[--color-gold]"
                            : "hover:border-[--color-blue]/40"
                        }
                      `}
                      onClick={(e) => {
                        e.stopPropagation();
                        onStudioSelect?.(studio.id);
                      }}
                    >
                      <span>Enter Studio</span>
                      <ArrowRight className="w-4 h-4 ml-2 inline transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-8">
          <div
            className="flex items-center gap-3 mb-4 animate-fade-in"
            style={{ animationDelay: "350ms", animationFillMode: "both" }}
          >
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <span className="text-xs uppercase tracking-[0.2em] text-[--text-muted] font-medium">
              Analyze Results
            </span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>

          <div
            className={`
              group relative rounded-lg border border-white/8 backdrop-blur-xl
              bg-gradient-to-br ${OUTPUT_STUDIO.gradient} bg-white/[0.015]
              transition-all duration-500 ease-out cursor-pointer
              hover:border-white/15 hover:scale-[1.01]
              ${OUTPUT_STUDIO.borderGlow}
              animate-fade-in
            `}
            style={{
              animationDelay: "400ms",
              animationFillMode: "both",
              animationDuration: "600ms",
            }}
            onClick={() => onStudioSelect?.(OUTPUT_STUDIO.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onStudioSelect?.(OUTPUT_STUDIO.id);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-[--color-blue]/5 via-transparent to-[--color-gold]/5 pointer-events-none" />

            <div className="relative p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
              <div
                className={`
                  w-14 h-14 md:w-16 md:h-16 rounded-lg flex items-center justify-center shrink-0
                  bg-gradient-to-br from-[--color-blue]/10 to-[--color-gold]/10
                  border border-white/10
                  transition-all duration-500
                  group-hover:border-white/20
                  ${OUTPUT_STUDIO.iconGlow}
                `}
              >
                <OUTPUT_STUDIO.icon className="w-7 h-7 md:w-8 md:h-8 text-[--color-gold] transition-transform duration-300 group-hover:scale-110" />
              </div>

              <div className="flex-1">
                <h3 className="font-poppins font-bold text-xl md:text-2xl text-white mb-2 tracking-tight">
                  {OUTPUT_STUDIO.name}
                </h3>

                <p className="text-[--text-muted] mb-3 leading-relaxed text-sm md:text-base max-w-2xl">
                  {OUTPUT_STUDIO.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {OUTPUT_STUDIO.audience.map((role) => (
                    <span
                      key={role}
                      className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-[--text-muted] border border-white/10"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="
                  px-8 py-2 rounded-lg font-medium text-sm text-white shrink-0
                  bg-white/5 border border-white/10
                  transition-all duration-300 group/btn
                  hover:bg-white/15 hover:border-[--color-gold]/30
                "
                onClick={(e) => {
                  e.stopPropagation();
                  onStudioSelect?.(OUTPUT_STUDIO.id);
                }}
              >
                <span>View Command Center</span>
                <ArrowRight className="w-4 h-4 ml-2 inline transition-transform duration-300 group-hover/btn:translate-x-1" />
              </button>
            </div>
          </div>
        </div>

        <div
          className="animate-fade-in"
          style={{ animationDelay: "550ms", animationFillMode: "both" }}
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-white/20" />
            <div className="w-2 h-2 rounded-full bg-[--color-gold]/60" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-white/20" />
          </div>

          <div className="flex items-center justify-center gap-2 text-[--text-muted]">
            <Sparkles className="w-4 h-4 text-[--color-gold]/60" />
            <span className="text-sm tracking-wide">
              Powered by{" "}
              <span className="text-[--color-gold]/80 font-medium">
                FalconAI
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
